"use client";

import React, { useEffect, useState, useMemo, useRef } from "react";
import { m, AnimatePresence } from "motion/react";
import {
  MapPin,
  Mail,
  Phone,
  Clock,
  Send,
  MessageSquare,
  ChevronDown,
  Calendar,
  Users,
  AlertCircle,
  Facebook,
  Instagram,
  Twitter,
  Linkedin,
  Youtube,
} from "lucide-react";
import Link from "next/link";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useAppDispatch, useAppSelector } from "@/hooks/hooks";
import { ISettings } from "@/types/home.types";
import {
  getAllActiveHotels,
  insertWeblInquiry,
} from "@/services/hotel.services";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import FAQ from "./FAQ";

export default function ContactUsClient() {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { HeaderMenuList } = useAppSelector((state) => state.home);

  // Load States from Redux
  const BannerList = useAppSelector((state) => state.home.BannerList);
  const SettingList = useAppSelector((state) => state.home.SettingList);
  const hotelLoading = useAppSelector((state) => state.hotel.InquiryLoading);
  const HotelList = useAppSelector((state) => state.hotel.HotelList);

  const loading = hotelLoading;
  const hasDispatched = useRef(false);

  // Dispatch lists if not loaded
  useEffect(() => {
    if (hasDispatched.current) return;

    const shouldDispatchHotel = HotelList.length === 0;

    if (shouldDispatchHotel) {
      hasDispatched.current = true;
      dispatch(
        getAllActiveHotels({
          HotelName: "",
          Limit: 300,
          Skip: 0,
        }),
      );
    }
  }, [dispatch, HotelList.length]);

  // Extract settings
  const Settings: Record<string, ISettings> = useMemo(() => {
    return (SettingList || []).reduce(
      (acc, item) => {
        acc[item?.SettingKey] = item;
        return acc;
      },
      {} as Record<string, ISettings>,
    );
  }, [SettingList]);

  // Settings values
  const PhoneNo = Settings?.["PhoneNo"]?.SettingValue || "+91 9099667119";
  const PhoneNo2 = Settings?.["PhoneNo2"]?.SettingValue || "";
  const PhoneNo3 = Settings?.["PhoneNo3"]?.SettingValue || "";
  const EmailID = Settings?.["EmailID"]?.SettingValue || "booking@aalpine.in";
  const EmailID2 = Settings?.["EmailID2"]?.SettingValue || "";
  const EmailID3 = Settings?.["EmailID3"]?.SettingValue || "";
  const Address =
    Settings?.["Address"]?.SettingValue ||
    "B-701/702, PNTC, Radio Mirchi Road, Satellite, Ahmedabad";
  const Address2 = Settings?.["Address2"]?.SettingValue || "";
  const Address3 = Settings?.["Address3"]?.SettingValue || "";
  const AddressMap = Settings?.["AddressMap"]?.SettingValue || "#";
  const AddressMap2 = Settings?.["AddressMap2"]?.SettingValue || "";
  const AddressMap3 = Settings?.["AddressMap3"]?.SettingValue || "";
  const AddressIframe = Settings?.["AddressIframe"]?.SettingValue;
  const AddressIframe2 = Settings?.["AddressIframe2"]?.SettingValue;
  const BusinessHours =
    Settings?.["BusinessHours"]?.SettingValue ||
    "Monday - Saturday: 9:00 AM - 7:00 PM";
  const BusinessHours2 =
    Settings?.["BusinessHours2"]?.SettingValue || "Sunday: 10:00 AM - 5:00 PM";
  const CompanyName =
    Settings?.["CompanyName"]?.SettingValue ||
    "Rann Utsav Luxury Booking Portal";

  // Social Links
  const FacebookLink = Settings?.["FacebookLink"]?.SettingValue || "";
  const InstaLink = Settings?.["InstaLink"]?.SettingValue || "";
  const XLink = Settings?.["XLink"]?.SettingValue || "";
  const LinkedInLink = Settings?.["LinkedInLink"]?.SettingValue || "";
  const YoutubeLink = Settings?.["YoutubeLink"]?.SettingValue || "";

  // Banners info
  const banner: any = useMemo(() => {
    return (
      (BannerList || []).find((b: any) => b?.BannerType === "contact") || {}
    );
  }, [BannerList]);

  // Formik Integration
  const formik = useFormik({
    initialValues: {
      FirstName: "",
      LastName: "",
      EmailID: "",
      MobileNo: "",
      InquiryType: "",
      PackageName: "",
      TravelDate: "",
      TotalPax: "2",
      TotalChild: "0",
      Remarks: "",
      Nights: "2 Nights",
    },
    validationSchema: Yup.object({
      FirstName: Yup.string().required("First Name is required"),
      LastName: Yup.string().required("Last Name is required"),
      EmailID: Yup.string()
        .email("Invalid email format")
        .required("Email is required"),
      MobileNo: Yup.string()
        .matches(/^[0-9]{10}$/, "Mobile number must be exactly 10 digits")
        .required("Mobile number is required"),
      InquiryType: Yup.string().required("Inquiry type is required"),
      Nights: Yup.string().required("Nights is required"),
      PackageName: Yup.string().when("InquiryType", {
        is: (val: string) => val !== "General Inquiry",
        then: (schema) =>
          schema.required("Please select a Package or Resort option"),
        otherwise: (schema) => schema.notRequired(),
      }),
      TravelDate: Yup.string().when("InquiryType", {
        is: (val: string) => val !== "General Inquiry",
        then: (schema) =>
          schema
            .required("Travel Date is required")
            .test("is-future", "Travel Date must be of future", (value) => {
              if (!value) return false;
              const tomorrow = new Date();
              tomorrow.setDate(tomorrow.getDate() + 1);
              const tomorrowStr =
                tomorrow.getFullYear() +
                "-" +
                String(tomorrow.getMonth() + 1).padStart(2, "0") +
                "-" +
                String(tomorrow.getDate()).padStart(2, "0");
              return value >= tomorrowStr;
            }),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
    onSubmit: async (values, { resetForm }) => {
      const payload = {
        FirstName: values.FirstName,
        LastName: values.LastName,
        CompanyName: "",
        Address: "",
        Email: values.EmailID,
        MobileNo: String(values.MobileNo),
        NoofPerson: values.TotalPax ? +values.TotalPax : 2,
        NoofChild: values.TotalChild ? +values.TotalChild : 0,
        CheckinDate:
          values.TravelDate || new Date().toISOString().split("T")[0],
        TypeYourTent: values.PackageName || values.InquiryType || "",
        ResortURL: "",
        PackageURL: "",
        Remarks: values.Remarks || "",
        Nights: values.Nights,
      };

      try {
        const response = await dispatch(insertWeblInquiry(payload));
        if (response.meta.requestStatus === "fulfilled") {
          toast.success(
            "Inquiry submitted successfully! Our expert will call you shortly.",
          );
          resetForm();
          setTimeout(() => {
            router.push("/thankyou/");
          }, 1500);
        } else {
          toast.error(
            (response.payload as any)?.message ||
              "Failed to submit inquiry. Please try again.",
          );
        }
      } catch (err) {
        console.error(err);
        toast.error("An unexpected error occurred. Please try again later.");
      }
    },
  });

  // Filter package options based on InquiryType selection
  const packageOptions = useMemo(() => {
    if (formik.values.InquiryType === "Dhordo") {
      return (HeaderMenuList?.DhordoTentCity || []).map((h: any) => ({
        id: h.ID,
        name: h.Title,
      }));
    }
    if (formik.values.InquiryType === "Dholavira") {
      return (HeaderMenuList?.DholaviraTentCity || []).map((h: any) => ({
        id: h.ID,
        name: h.Title,
      }));
    }
    if (formik.values.InquiryType === "Rann Packages") {
      return (HeaderMenuList?.PackageList || []).map((h: any) => ({
        id: h.ID,
        name: h.Title,
      }));
    }

    return [];
  }, [formik.values.InquiryType, HeaderMenuList]);

  // Reset PackageName when InquiryType changes
  useEffect(() => {
    formik.setFieldValue("PackageName", "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.InquiryType]);

  const mapContent = useMemo(() => {
    if (AddressIframe) {
      let iframeStr = AddressIframe;
      iframeStr = iframeStr.replace(/\s?width="[^"]*"/g, "");
      iframeStr = iframeStr.replace(/\s?height="[^"]*"/g, "");
      iframeStr = iframeStr.replace(
        "<iframe ",
        '<iframe width="100%" height="100%" loading="lazy" ',
      );
      return { __html: iframeStr };
    }
    return undefined;
  }, [AddressIframe]);

  return (
    <>
      <Toaster position="top-right" richColors />

      {/* Global Preloader Overlay */}
      <AnimatePresence>
        {(loading || formik.isSubmitting) && (
          <m.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-md z-[9999] flex flex-col items-center justify-center"
          >
            <div className="relative flex items-center justify-center">
              <div className="w-20 h-20 border-4 border-gray-200 border-t-pink-500 rounded-full animate-spin"></div>
              <span className="absolute animate-pulse text-white text-xs font-bold tracking-widest uppercase">
                Kutch
              </span>
            </div>
            <m.p
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="mt-6 text-xl text-yellow-300 font-sans tracking-wide font-medium"
            >
              {formik.isSubmitting
                ? "Sending Your Request..."
                : "Loading Desert Splendors..."}
            </m.p>
          </m.div>
        )}
      </AnimatePresence>

      <div className="min-h-screen bg-white">
        {/* Banner Section */}
        <section className="relative h-[45vh] flex items-center justify-center overflow-hidden">
          {/* Background image & Overlay */}
          <div className="absolute inset-0">
            <Image
              src={
                banner?.BannerImage ||
                "https://images.unsplash.com/photo-1670923331633-be262e035a9a?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=1920"
              }
              alt={
                banner?.AltTitle ||
                banner?.BannerTitle ||
                "Contact Official Rann Utsav Team"
              }
              fill
              priority
              className="object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black/80"></div>
          </div>

          <div className="relative z-10 text-center px-4 max-w-4xl">
            <m.span
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-block px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-yellow-300 font-semibold text-sm uppercase tracking-wider mb-4"
            >
              Reach Our Kutchi Specialists
            </m.span>

            <m.h1
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.1 }}
              className="text-4xl md:text-6xl font-bold text-white mb-6 drop-shadow-lg font-sans"
            >
              {banner?.BannerTitle || "Get in Touch with Us"}
            </m.h1>

            <m.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-lg md:text-xl text-gray-200 font-sans font-light drop-shadow-md leading-relaxed"
            >
              {banner?.Description ||
                "Plan your ultra-luxury journey to the White Desert Festival of Kutch, Gujarat. We are here to customize your perfect stay."}
            </m.p>
          </div>
        </section>

        {/* Contact Info Cards */}
        <section className="py-16 bg-gradient-to-b from-white to-gray-50 relative z-20 -mt-10">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Card 1: Visit Office */}
              <m.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="bg-white rounded-3xl shadow-xl p-8 border border-gray-100/60 hover:shadow-2xl transition-all duration-300 flex flex-col justify-between group transform hover:-translate-y-1"
              >
                <div>
                  <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-yellow-400 via-pink-500 to-purple-600 rounded-2xl mb-6 shadow-lg shadow-pink-500/20 transform group-hover:scale-110 transition-transform duration-300">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 font-sans">
                    Our Offices
                  </h3>
                  <p className="text-pink-600 font-semibold text-xs uppercase tracking-wider mb-2 font-sans">
                    {CompanyName}
                  </p>

                  <div className="space-y-4 text-sm text-gray-600 font-sans leading-relaxed">
                    {Address && (
                      <p className="hover:text-pink-600 transition">
                        <Link
                          href={AddressMap}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {Address}
                        </Link>
                      </p>
                    )}
                    {Address2 && (
                      <p className="border-t border-gray-100 pt-3 hover:text-pink-600 transition">
                        <Link
                          href={AddressMap2 || "#"}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {Address2}
                        </Link>
                      </p>
                    )}
                    {Address3 && (
                      <p className="border-t border-gray-100 pt-3 hover:text-pink-600 transition">
                        <Link
                          href={AddressMap3 || "#"}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {Address3}
                        </Link>
                      </p>
                    )}
                  </div>
                </div>
              </m.div>

              {/* Card 2: Email Us */}
              <m.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="bg-white rounded-3xl shadow-xl p-8 border border-gray-100/60 hover:shadow-2xl transition-all duration-300 flex flex-col justify-between group transform hover:-translate-y-1"
              >
                <div>
                  <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-pink-500 to-purple-600 rounded-2xl mb-6 shadow-lg shadow-purple-500/20 transform group-hover:scale-110 transition-transform duration-300">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 font-sans">
                    Email Address
                  </h3>
                  <p className="text-purple-600 font-semibold text-xs uppercase tracking-wider mb-2 font-sans">
                    24/7 Digital Support
                  </p>

                  <div className="space-y-3 text-sm text-gray-600 font-sans">
                    {EmailID && (
                      <p className="hover:text-pink-600 transition font-medium">
                        <Link href={`mailto:${EmailID}`}>{EmailID}</Link>
                      </p>
                    )}
                    {EmailID2 && (
                      <p className="border-t border-gray-100 pt-3 hover:text-pink-600 transition font-medium">
                        <Link href={`mailto:${EmailID2}`}>{EmailID2}</Link>
                      </p>
                    )}
                    {EmailID3 && (
                      <p className="border-t border-gray-100 pt-3 hover:text-pink-600 transition font-medium">
                        <Link href={`mailto:${EmailID3}`}>{EmailID3}</Link>
                      </p>
                    )}
                  </div>
                </div>
              </m.div>

              {/* Card 3: Call Us */}
              <m.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="bg-white rounded-3xl shadow-xl p-8 border border-gray-100/60 hover:shadow-2xl transition-all duration-300 flex flex-col justify-between group transform hover:-translate-y-1"
              >
                <div>
                  <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-yellow-400 to-pink-500 rounded-2xl mb-6 shadow-lg shadow-yellow-500/20 transform group-hover:scale-110 transition-transform duration-300">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 font-sans">
                    Phone Helpline
                  </h3>
                  <p className="text-yellow-600 font-semibold text-xs uppercase tracking-wider mb-2 font-sans">
                    Instant Connection
                  </p>

                  <div className="space-y-3 text-sm text-gray-600 font-sans">
                    {PhoneNo && (
                      <p className="hover:text-pink-600 transition font-semibold text-base">
                        <Link href={`tel:${PhoneNo}`}>{PhoneNo}</Link>
                      </p>
                    )}
                    {PhoneNo2 && (
                      <p className="border-t border-gray-100 pt-3 hover:text-pink-600 transition font-semibold text-base">
                        <Link href={`tel:${PhoneNo2}`}>{PhoneNo2}</Link>
                      </p>
                    )}
                    {PhoneNo3 && (
                      <p className="border-t border-gray-100 pt-3 hover:text-pink-600 transition font-semibold text-base">
                        <Link href={`tel:${PhoneNo3}`}>{PhoneNo3}</Link>
                      </p>
                    )}
                  </div>
                </div>
              </m.div>

              {/* Card 4: Working Hours */}
              <m.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="bg-white rounded-3xl shadow-xl p-8 border border-gray-100/60 hover:shadow-2xl transition-all duration-300 flex flex-col justify-between group transform hover:-translate-y-1"
              >
                <div>
                  <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-purple-600 via-pink-500 to-yellow-400 rounded-2xl mb-6 shadow-lg shadow-purple-500/20 transform group-hover:scale-110 transition-transform duration-300">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 font-sans">
                    Business Hours
                  </h3>
                  <p className="text-pink-600 font-semibold text-xs uppercase tracking-wider mb-2 font-sans">
                    Working Schedule
                  </p>

                  <div className="space-y-3 text-sm text-gray-600 font-sans leading-relaxed">
                    <p className="font-medium text-gray-800">{BusinessHours}</p>
                    {BusinessHours2 && (
                      <p className="border-t border-gray-100 pt-3 font-medium text-gray-800">
                        {BusinessHours2}
                      </p>
                    )}
                  </div>
                </div>
              </m.div>
            </div>
          </div>
        </section>

        {/* Dynamic Form & Interactive Map */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid lg:grid-cols-12 gap-12 items-start">
              {/* Inquiry Form */}
              <m.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="lg:col-span-7"
              >
                <div className="bg-gradient-to-br from-pink-50/50 via-white to-purple-50/40 rounded-3xl shadow-2xl p-8 md:p-10 border border-pink-100/50">
                  <h2 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3 font-sans">
                    <MessageSquare className="w-8 h-8 text-pink-600" />
                    Custom Desert Journey Plan
                  </h2>
                  <p className="text-gray-600 mb-8 font-sans">
                    Fill out the form below, and our specialized travel curators
                    will build your personalized Kutch luxury holiday itinerary.
                  </p>

                  <form onSubmit={formik.handleSubmit} className="space-y-5">
                    {/* Name Fields */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5 font-sans">
                          First Name *
                        </label>
                        <input
                          name="FirstName"
                          type="text"
                          placeholder="Your First Name"
                          value={formik.values.FirstName}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className={`w-full px-3 py-3 border-2 ${formik.touched.FirstName && formik.errors.FirstName ? "border-red-500 focus:ring-red-500/20" : "border-gray-200 focus:border-pink-500 focus:ring-pink-500/20"} rounded-2xl outline-none transition-all duration-300 bg-white font-sans text-sm`}
                        />
                        {formik.touched.FirstName &&
                          formik.errors.FirstName && (
                            <span className="text-red-500 text-xs mt-1.5 flex items-center gap-1 font-sans">
                              <AlertCircle className="w-3.5 h-3.5" />
                              {formik.errors.FirstName as string}
                            </span>
                          )}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5 font-sans">
                          Last Name
                        </label>
                        <input
                          name="LastName"
                          type="text"
                          placeholder="Your Last Name"
                          value={formik.values.LastName}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className={`w-full px-3 py-3 border-2 ${formik.touched.LastName && formik.errors.LastName ? "border-red-500 focus:ring-red-500/20" : "border-gray-200 focus:border-pink-500 focus:ring-pink-500/20"} rounded-2xl outline-none transition-all duration-300 bg-white font-sans text-sm`}
                        />
                        {formik.touched.LastName && formik.errors.LastName && (
                          <span className="text-red-500 text-xs mt-1.5 flex items-center gap-1 font-sans">
                            <AlertCircle className="w-3.5 h-3.5" />
                            {formik.errors.LastName as string}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Email & Mobile */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5 font-sans">
                          Email Address *
                        </label>
                        <input
                          name="EmailID"
                          type="email"
                          placeholder="your@email.com"
                          value={formik.values.EmailID}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className={`w-full px-3 py-3 border-2 ${formik.touched.EmailID && formik.errors.EmailID ? "border-red-500 focus:ring-red-500/20" : "border-gray-200 focus:border-pink-500 focus:ring-pink-500/20"} rounded-2xl outline-none transition-all duration-300 bg-white font-sans text-sm`}
                        />
                        {formik.touched.EmailID && formik.errors.EmailID && (
                          <span className="text-red-500 text-xs mt-1.5 flex items-center gap-1 font-sans">
                            <AlertCircle className="w-3.5 h-3.5" />
                            {formik.errors.EmailID as string}
                          </span>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5 font-sans">
                          Phone Number *
                        </label>
                        <input
                          name="MobileNo"
                          type="tel"
                          maxLength={10}
                          placeholder="10-digit mobile no"
                          value={formik.values.MobileNo}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className={`w-full px-3 py-3 border-2 ${formik.touched.MobileNo && formik.errors.MobileNo ? "border-red-500 focus:ring-red-500/20" : "border-gray-200 focus:border-pink-500 focus:ring-pink-500/20"} rounded-2xl outline-none transition-all duration-300 bg-white font-sans text-sm`}
                        />
                        {formik.touched.MobileNo && formik.errors.MobileNo && (
                          <span className="text-red-500 text-xs mt-1.5 flex items-center gap-1 font-sans">
                            <AlertCircle className="w-3.5 h-3.5" />
                            {formik.errors.MobileNo as string}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Inquiry Type & Dynamic Selectors */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5 font-sans">
                          Inquiry Type *
                        </label>
                        <div className="relative">
                          <select
                            name="InquiryType"
                            value={formik.values.InquiryType}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            className={`w-full px-3 py-3 border-2 ${formik.touched.InquiryType && formik.errors.InquiryType ? "border-red-500 focus:ring-red-500/20" : "border-gray-200 focus:border-pink-500 focus:ring-pink-500/20"} rounded-2xl outline-none transition-all duration-300 bg-white font-sans text-sm appearance-none cursor-pointer`}
                          >
                            <option value="">Select Inquiry Type</option>
                            <option value="Dhordo">Dhordo Tent City</option>
                            <option value="Dholavira">
                              Dholavira Tent City
                            </option>
                            <option value="Rann Packages">Rann Packages</option>
                            <option value="General Inquiry">
                              General Inquiry
                            </option>
                          </select>
                          <ChevronDown className="w-5 h-5 text-gray-400 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" />
                        </div>
                        {formik.touched.InquiryType &&
                          formik.errors.InquiryType && (
                            <span className="text-red-500 text-xs mt-1.5 flex items-center gap-1 font-sans">
                              <AlertCircle className="w-3.5 h-3.5" />
                              {formik.errors.InquiryType as string}
                            </span>
                          )}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5 font-sans">
                          Package / Resort Option *
                        </label>
                        <div className="relative">
                          <select
                            name="PackageName"
                            value={formik.values.PackageName}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            disabled={!formik.values.InquiryType}
                            className={`w-full px-3 py-3 border-2 ${formik.touched.PackageName && formik.errors.PackageName ? "border-red-500 focus:ring-red-500/20" : "border-gray-200 focus:border-pink-500 focus:ring-pink-500/20"} rounded-2xl outline-none transition-all duration-300 bg-white font-sans text-sm appearance-none cursor-pointer disabled:bg-gray-50 disabled:cursor-not-allowed`}
                          >
                            <option value="">Select Option</option>
                            {packageOptions.map((opt) => (
                              <option key={opt.id} value={opt.name}>
                                {opt.name}
                              </option>
                            ))}
                          </select>
                          <ChevronDown className="w-5 h-5 text-gray-400 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" />
                        </div>
                        {formik.touched.PackageName &&
                          formik.errors.PackageName && (
                            <span className="text-red-500 text-xs mt-1.5 flex items-center gap-1 font-sans">
                              <AlertCircle className="w-3.5 h-3.5" />
                              {formik.errors.PackageName as string}
                            </span>
                          )}
                      </div>
                    </div>

                    {/* Pax details & Travel date & Nights */}
                    <div className="grid md:grid-cols-4 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5 font-sans flex items-center gap-1.5">
                          <Calendar className="w-4 h-4 text-pink-500" />
                          Travel Date *
                        </label>
                        <input
                          name="TravelDate"
                          type="date"
                          min={(() => {
                            const tmrw = new Date();
                            tmrw.setDate(tmrw.getDate() + 1);
                            return (
                              tmrw.getFullYear() +
                              "-" +
                              String(tmrw.getMonth() + 1).padStart(2, "0") +
                              "-" +
                              String(tmrw.getDate()).padStart(2, "0")
                            );
                          })()}
                          value={formik.values.TravelDate}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className={`w-full px-3 py-3 border-2 ${formik.touched.TravelDate && formik.errors.TravelDate ? "border-red-500 focus:ring-red-500/20" : "border-gray-200 focus:border-pink-500 focus:ring-pink-500/20"} rounded-2xl outline-none transition-all duration-300 bg-white font-sans text-sm cursor-pointer`}
                        />
                        {formik.touched.TravelDate &&
                          formik.errors.TravelDate && (
                            <span className="text-red-500 text-xs mt-1.5 flex items-center gap-1 font-sans">
                              <AlertCircle className="w-3.5 h-3.5" />
                              {formik.errors.TravelDate as string}
                            </span>
                          )}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5 font-sans flex items-center gap-1.5">
                          <Calendar className="w-4 h-4 text-pink-500" />
                          Nights *
                        </label>
                        <div className="relative">
                          <select
                            name="Nights"
                            value={formik.values.Nights}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            className="w-full px-3 py-3 border-2 border-gray-200 focus:border-pink-500 focus:ring-pink-500/20 rounded-2xl outline-none transition-all duration-300 bg-white font-sans text-sm appearance-none cursor-pointer"
                          >
                            <option value="1 Nights">1 Nights</option>
                            <option value="2 Nights">2 Nights</option>
                            <option value="3 Nights">3 Nights</option>
                          </select>
                          <ChevronDown className="w-5 h-5 text-gray-400 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5 font-sans flex items-center gap-1.5">
                          <Users className="w-4 h-4 text-pink-500" />
                          Adults Count
                        </label>
                        <select
                          name="TotalPax"
                          value={formik.values.TotalPax}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className="w-full px-3 py-3 border-2 border-gray-200 focus:border-pink-500 focus:ring-pink-500/20 rounded-2xl outline-none transition-all duration-300 bg-white font-sans text-sm appearance-none cursor-pointer"
                        >
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                            <option key={n} value={String(n)}>
                              {n} {n === 1 ? "Adult" : "Adults"}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5 font-sans flex items-center gap-1.5">
                          <Users className="w-4 h-4 text-pink-500" />
                          Children Count
                        </label>
                        <select
                          name="TotalChild"
                          value={formik.values.TotalChild}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className="w-full px-3 py-3 border-2 border-gray-200 focus:border-pink-500 focus:ring-pink-500/20 rounded-2xl outline-none transition-all duration-300 bg-white font-sans text-sm appearance-none cursor-pointer"
                        >
                          {[0, 1, 2, 3, 4, 5].map((n) => (
                            <option key={n} value={String(n)}>
                              {n} {n === 1 ? "Child" : "Children"}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Remarks */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5 font-sans">
                        Any Special Requirements?
                      </label>
                      <textarea
                        name="Remarks"
                        rows={3}
                        placeholder="Tell us about your preferences..."
                        value={formik.values.Remarks}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className="w-full px-3 py-3 border-2 border-gray-200 focus:border-pink-500 focus:ring-pink-500/20 rounded-2xl outline-none transition-all duration-300 bg-white font-sans text-sm resize-none"
                      />
                    </div>

                    {/* Button */}
                    <button
                      type="submit"
                      disabled={formik.isSubmitting}
                      className="w-full py-4 bg-gradient-to-r from-pink-500 to-purple-600 text-white font-bold text-lg rounded-xl shadow-lg shadow-pink-500/30 hover:shadow-xl hover:shadow-pink-500/50 transition-all duration-300 hover:scale-[1.02] active:scale-95 flex items-center justify-center gap-2 font-sans"
                    >
                      {formik.isSubmitting ? (
                        <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                      ) : (
                        <Send className="w-5 h-5" />
                      )}
                      {formik.isSubmitting ? "Submitting..." : "Submit Inquiry"}
                    </button>

                    <p className="text-xs text-gray-500 text-center font-sans">
                      We protect your personal data. Our coordinator will
                      contact you inside 24 hours.
                    </p>
                  </form>
                </div>
              </m.div>

              {/* Map & Office Locations */}
              <m.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="lg:col-span-5 space-y-8"
              >
                {/* Google Map Section */}
                <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100 h-[380px] w-full relative group">
                  <div
                    className="w-full h-full"
                    dangerouslySetInnerHTML={mapContent}
                  />
                  {/* <div className="absolute bottom-4 left-4 right-4 bg-white/90 backdrop-blur-md px-5 py-3 rounded-2xl shadow-lg border border-white/20 pointer-events-none">
                    <p className="text-xs font-bold text-pink-600 uppercase tracking-widest mb-0.5">
                      Festival Location
                    </p>
                    <p className="text-sm font-bold text-gray-900">
                      White Desert Resort (Tent City), Dhordo, Kutch
                    </p>
                  </div> */}
                </div>

                {/* Sub Map for Ahmedabad Office */}
                {/* {AddressIframe2 && (
                  <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100 h-[280px] w-full relative group">
                    <div
                      className="w-full h-full"
                      dangerouslySetInnerHTML={mapContent2}
                    />
                    <div className="absolute bottom-4 left-4 right-4 bg-white/90 backdrop-blur-md px-5 py-3 rounded-2xl shadow-lg border border-white/20 pointer-events-none">
                      <p className="text-xs font-bold text-purple-600 uppercase tracking-widest mb-0.5">
                        Corporate Office
                      </p>
                      <p className="text-sm font-bold text-gray-900">
                        Head Office, Ahmedabad, Gujarat
                      </p>
                    </div>
                  </div>
                )} */}

                {/* Direct Call & Socials Quick Card */}
                <div className="bg-gradient-to-br from-pink-50/70 via-purple-50/60 to-yellow-50/40 rounded-3xl shadow-xl p-8 border border-pink-100/50 relative overflow-hidden">
                  {/* Subtle decorative circles */}
                  <div className="absolute -right-16 -top-16 w-36 h-36 bg-pink-300/10 rounded-full blur-2xl"></div>
                  <div className="absolute -left-16 -bottom-16 w-36 h-36 bg-yellow-300/10 rounded-full blur-2xl"></div>

                  <h3 className="text-2xl font-bold mb-4 font-sans bg-gradient-to-r from-yellow-500 via-pink-600 to-purple-600 bg-clip-text text-transparent">
                    Quick Assistance?
                  </h3>
                  <p className="text-gray-600 text-sm mb-6 leading-relaxed font-sans font-light">
                    Call our travel curators directly to check live room
                    availability, book full moon dates, or request custom event
                    entries.
                  </p>

                  <div className="space-y-4">
                    <div className="flex items-center gap-4 p-4 bg-white/80 hover:bg-white/95 rounded-2xl border border-pink-100/40 shadow-sm transition-all duration-300 hover:shadow-md group/btn">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-yellow-400 to-pink-500 flex items-center justify-center shadow-lg transform group-hover/btn:scale-105 transition-transform">
                        <Phone className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-xs text-pink-600 font-sans font-semibold">
                          Hotline Helpline
                        </p>
                        <p className="font-bold text-gray-800 text-base font-sans hover:text-pink-600 transition">
                          <Link href={`tel:${PhoneNo}`}>{PhoneNo}</Link>
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 p-4 bg-white/80 hover:bg-white/95 rounded-2xl border border-pink-100/40 shadow-sm transition-all duration-300 hover:shadow-md group/btn">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-pink-500 to-purple-600 flex items-center justify-center shadow-lg transform group-hover/btn:scale-105 transition-transform">
                        <Mail className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-xs text-purple-600 font-sans font-semibold">
                          Official Email
                        </p>
                        <p className="font-bold text-gray-800 text-base break-all font-sans hover:text-pink-600 transition">
                          <Link href={`mailto:${EmailID}`}>{EmailID}</Link>
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Social Connections */}
                  <div className="mt-8 pt-6 border-t border-pink-100/60">
                    <p className="text-xs text-gray-400 uppercase tracking-widest text-center mb-4 font-sans font-semibold">
                      Join Our Social Circle
                    </p>
                    <div className="flex justify-center gap-4">
                      {[
                        {
                          link: FacebookLink,
                          name: "Facebook",
                          icon: Facebook,
                          bg: "hover:bg-[#1877F2] text-gray-600 hover:text-white",
                        },
                        {
                          link: InstaLink,
                          name: "Instagram",
                          icon: Instagram,
                          bg: "hover:bg-gradient-to-tr hover:from-[#f9ce34] hover:via-[#ee2a7b] hover:to-[#6228d7] text-gray-600 hover:text-white",
                        },
                        {
                          link: XLink,
                          name: "Twitter",
                          icon: Twitter,
                          bg: "hover:bg-black text-gray-600 hover:text-white",
                        },
                        {
                          link: LinkedInLink,
                          name: "LinkedIn",
                          icon: Linkedin,
                          bg: "hover:bg-[#0A66C2] text-gray-600 hover:text-white",
                        },
                        {
                          link: YoutubeLink,
                          name: "Youtube",
                          icon: Youtube,
                          bg: "hover:bg-[#FF0000] text-gray-600 hover:text-white",
                        },
                      ]
                        ?.filter((social: any) => social.link !== "")
                        ?.map((item, index) => {
                          const IconComponent = item.icon;
                          return (
                            <Link
                              key={index}
                              href={item.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`w-10 h-10 bg-white border border-pink-100/30 rounded-full flex items-center justify-center shadow-sm transition-all duration-300 hover:scale-110 ${item.bg}`}
                              aria-label={`Visit our ${item.name}`}
                            >
                              <IconComponent className="w-5 h-5" />
                            </Link>
                          );
                        })}
                    </div>
                  </div>
                </div>
              </m.div>
            </div>
          </div>
        </section>

        {/* Dynamic FAQ Component */}
        <FAQ />

        {/* Beautiful Footer CTA */}
        <section className="py-20 bg-gradient-to-br from-yellow-400 via-pink-500 to-purple-600 text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="absolute -left-32 -bottom-32 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>

          <div className="max-w-5xl mx-auto px-4 text-center relative z-10">
            <m.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl md:text-4xl font-bold mb-6 font-sans">
                Ready to Experience the Magic of Kutch?
              </h2>
              <p className="text-md md:text-lg mb-10 max-w-3xl mx-auto text-white/90 font-sans font-light leading-relaxed">
                Contact our customer support team right now. We promise an
                authentic Kutchi royal welcome and an unforgettable memory!
              </p>

              <div className="flex flex-wrap gap-4 justify-center">
                <Link
                  href={`tel:${PhoneNo}`}
                  className="px-8 py-4 bg-white text-pink-600 font-bold rounded-full hover:shadow-2xl hover:shadow-white/20 transition-all duration-300 hover:scale-105 flex items-center gap-2 font-sans"
                >
                  <Phone className="w-5 h-5" />
                  Call Helpline
                </Link>
                <Link
                  href={`mailto:${EmailID}`}
                  className="px-8 py-4 bg-white/10 backdrop-blur-md text-white font-bold rounded-full border-2 border-white/30 hover:bg-white/20 transition-all duration-300 hover:scale-105 flex items-center gap-2 font-sans"
                >
                  <Mail className="w-5 h-5" />
                  Email Support
                </Link>
              </div>
            </m.div>
          </div>
        </section>
      </div>
    </>
  );
}
